import React, { useCallback, useEffect, useRef } from 'react'
import classNames from 'classnames'
import { Case, Switch } from 'react-if'
import { GuildScore } from '@/atoms/GuildScore'
import { Image } from '@/atoms/Image'
import { CaptionLG, CaptionMD } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { useIntersectionObserver } from '@/hooks/useIntersectionObserver'
import {
  SuggestedRailItemInteraction,
  useContentInteraction,
  HOVER_THRESHOLD_MS,
} from '@/services/Analytics/ContentInteractions'
import { useRecommendationTracking } from '@/services/RecommendedBannerService/useRecommendationTracking'
import { RelatedTitleContentV2 } from '@/types/codegen-federation'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useSafeTrack } from '@/utils/analytics'
import { Translate, useTranslate } from '@/utils/translate/translate-client'

export interface SuggestedContentCardProps {
  recommendedItem?: RelatedTitleContentV2
  itemIndex?: number
  className?: string
  pagePlacement?: number
}

export const SuggestedContentCard: React.FC<SuggestedContentCardProps> = ({
  recommendedItem,
  itemIndex = 0,
  className,
  pagePlacement = 0,
}) => {
  const { trackInteraction } = useContentInteraction()
  const { trackRecommendation } = useRecommendationTracking()
  const { t } = useTranslate('watch')
  const track = useSafeTrack()
  const [cardRef, isVisible] = useIntersectionObserver<HTMLDivElement>({
    threshold: 0.9, // Track when at least 90% of the card is visible
  })

  // Hover tracking state
  const hoverStartTimeRef = useRef<number | null>(null)
  const impressionTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const hasTrackedImpression = useRef(false)

  const handleRecommendationClick = useCallback(
    (slug?: string | null) => {
      track('Suggested Shows and Films Item Clicked', { projectSlug: slug ?? '', context: 'Watch Project Page' })
    },
    [track],
  )

  const onClick = useCallback(() => {
    if (!recommendedItem) return
    handleRecommendationClick(recommendedItem?.title?.project?.slug)
    trackRecommendation({ type: 'PRESS', items: [{ id: recommendedItem.id as string }] })

    // Skip interaction tracking if required data is missing
    if (!recommendedItem.title?.id || !recommendedItem.title?.image?.cloudinaryPath) return

    const interaction = new SuggestedRailItemInteraction({
      id: recommendedItem.title.id,
      __typename: recommendedItem.title.__typename ?? null,
      attribution: recommendedItem.attribution,
    })
      .withSurface('brand_landing')
      .withPlacement('suggested_tab', pagePlacement, itemIndex, 'horizontal_swimlane')
      .withImageArtwork(recommendedItem.title.image.cloudinaryPath, 'portrait_display')
      .withPress()

    trackInteraction(interaction)
  }, [handleRecommendationClick, trackRecommendation, recommendedItem, trackInteraction, itemIndex, pagePlacement])

  const handleMouseEnter = useCallback(() => {
    if (!recommendedItem) return

    // Record the start time
    hoverStartTimeRef.current = Date.now()
  }, [recommendedItem])

  const handleMouseLeave = useCallback(() => {
    if (!recommendedItem || !hoverStartTimeRef.current) return

    const dwellTime = Date.now() - hoverStartTimeRef.current

    // Only track hover if the user hovered for at least the threshold time
    if (dwellTime >= HOVER_THRESHOLD_MS) {
      // Skip interaction tracking if required data is missing
      if (recommendedItem.title?.id && recommendedItem.title?.image?.cloudinaryPath) {
        const interaction = new SuggestedRailItemInteraction({
          id: recommendedItem.title.id,
          __typename: recommendedItem.title.__typename ?? null,
          attribution: recommendedItem.attribution,
        })
          .withSurface('brand_landing')
          .withPlacement('suggested_tab', pagePlacement, itemIndex, 'horizontal_swimlane')
          .withImageArtwork(recommendedItem.title.image.cloudinaryPath, 'portrait_display')
          .withHover(dwellTime)

        trackInteraction(interaction)
      }
    }

    // Reset state
    hoverStartTimeRef.current = null
  }, [recommendedItem, pagePlacement, itemIndex, trackInteraction])

  // Track impression when the card becomes visible for at least 1 second
  useEffect(() => {
    if (!recommendedItem || hasTrackedImpression.current) return

    if (isVisible) {
      // Start a timer to track impression after 1 second
      impressionTimeoutRef.current = setTimeout(() => {
        hasTrackedImpression.current = true
        trackRecommendation({ type: 'IMPRESSION', items: [{ id: recommendedItem.id as string }] })

        // Skip interaction tracking if required data is missing
        if (!recommendedItem.title?.id || !recommendedItem.title?.image?.cloudinaryPath) return

        const interaction = new SuggestedRailItemInteraction({
          id: recommendedItem.title.id,
          __typename: recommendedItem.title.__typename ?? null,
          attribution: recommendedItem.attribution,
        })
          .withSurface('brand_landing')
          .withPlacement('suggested_tab', pagePlacement, itemIndex, 'horizontal_swimlane')
          .withImageArtwork(recommendedItem.title.image.cloudinaryPath, 'portrait_display')
          .withImpression()

        trackInteraction(interaction)
      }, 1000) // Wait 1 second before tracking impression
    } else {
      // Clear timeout if item becomes not visible before 1 second
      if (impressionTimeoutRef.current) {
        clearTimeout(impressionTimeoutRef.current)
        impressionTimeoutRef.current = null
      }
    }

    return () => {
      if (impressionTimeoutRef.current) {
        clearTimeout(impressionTimeoutRef.current)
      }
    }
  }, [isVisible, trackRecommendation, recommendedItem, trackInteraction, itemIndex, pagePlacement])

  if (!recommendedItem || !recommendedItem?.title?.image?.cloudinaryPath) return null

  return (
    <div
      ref={cardRef}
      aria-label="suggested-content-card"
      id={recommendedItem?.id ?? ''}
      className="relative min-w-[250px] pr-6 lg:min-w-[316px]"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className={classNames('flex flex-col', className)}>
        {recommendedItem?.title?.image?.cloudinaryPath && (
          <a
            aria-label={recommendedItem.title?.name ?? ''}
            href={`${paths.watch.index}/${recommendedItem?.title?.project?.slug}`}
            className="aspect-h-3 aspect-w-2 relative mb-3 w-full cursor-pointer overflow-hidden rounded-2xl"
            onClick={onClick}
          >
            <div className="absolute inset-0">
              <Image
                alt={recommendedItem?.title?.name ?? ''}
                src={getCloudinaryImageUrl({ path: recommendedItem?.title?.image?.cloudinaryPath })}
                width={316}
                height={474}
                className="h-full w-full object-cover"
              />
            </div>
          </a>
        )}
        <div className="flex flex-col gap-y-1">
          <CaptionMD color="core-gray-500">
            <Switch>
              <Case condition={recommendedItem?.title?.__typename === 'ContentSeries'}>
                <Translate i18nKey="series" t={t}>
                  Series
                </Translate>
              </Case>
              <Case condition={recommendedItem?.title?.__typename === 'ContentMovie'}>
                <Translate i18nKey="movie" t={t}>
                  Movie
                </Translate>
              </Case>
              <Case condition={recommendedItem?.title?.__typename === 'ContentSpecial'}>
                <Translate i18nKey="special" t={t}>
                  Special
                </Translate>
              </Case>
            </Switch>
          </CaptionMD>
          <CaptionLG weight="bold">{recommendedItem?.title?.name}</CaptionLG>
          {recommendedItem?.title?.project?.highestScore ? (
            <GuildScore guildScore={recommendedItem?.title?.project?.highestScore} />
          ) : null}
        </div>
      </div>
    </div>
  )
}
